import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useCallback } from 'react';
import styled from 'styled-components';

import { CardImg } from '@sorare/core/src/components/card/CardImg';

import { AnimatingSlot } from './AnimatingSlot';
import { PlaceholderSlot } from './PlaceholderSlot';
import { TeamSlot } from './TeamSlot';
import type {
  CollectionAnimationClub_anyCardInterface,
  CollectionAnimationClub_cardCollection,
} from './__generated__/index.graphql';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: var(--unit);
  padding: var(--double-unit);
  background-color: var(--c-black);
  overflow: hidden;
`;

const TeamItemWrapper = styled.div`
  grid-column: span 2;
  grid-row: span 2;
`;

type Props = {
  cardCollection: CollectionAnimationClub_cardCollection;
  animatingCardIndex: number | undefined;
  onCardAnimationEnd: () => void;
  newCards: CollectionAnimationClub_anyCardInterface[];
  ref: (ref: HTMLDivElement, index: number) => void;
  index: number;
};

export const CollectionAnimationClub = ({
  cardCollection,
  animatingCardIndex,
  onCardAnimationEnd,
  newCards,
  ref,
  index,
}: Props) => {
  const { userCardCollection } = cardCollection;
  const slots = userCardCollection?.slots || [];

  const currentAnimatingCardSlug =
    animatingCardIndex === undefined
      ? undefined
      : newCards.at(animatingCardIndex)?.slug;
  const getAnimatingCardIndexInCollection = () => {
    if (!currentAnimatingCardSlug) {
      return undefined;
    }
    const cardBelongsToCollection =
      cardCollection.userCardCollection.slots.some(
        slot =>
          slot.highlightedCardCollectionCard?.anyCard.slug ===
          currentAnimatingCardSlug
      );
    if (!cardBelongsToCollection) {
      return undefined;
    }
    return newCards
      .slice(0, animatingCardIndex)
      .filter(card =>
        cardCollection.userCardCollection.slots.some(
          slot => slot.highlightedCardCollectionCard?.anyCard.slug === card.slug
        )
      ).length;
  };

  const collectionAnimatingCardIndex = getAnimatingCardIndexInCollection();

  const finalRef = useCallback(
    (r: Nullable<HTMLDivElement>) => {
      if (r) {
        ref(r, index);
      }
    },
    [ref, index]
  );

  return (
    <Grid ref={finalRef}>
      <TeamItemWrapper>
        <TeamSlot
          cardCollection={cardCollection}
          newCards={newCards}
          animatingCardIndex={collectionAnimatingCardIndex}
        />
      </TeamItemWrapper>
      {slots.map(slot => {
        const isFilled = !!slot.cardCollectionCardsCount;
        const newCardIndex = newCards.findIndex(
          card => card.slug === slot.highlightedCardCollectionCard?.anyCard.slug
        );
        const isNewCardSlot = newCardIndex !== -1;

        if (isNewCardSlot) {
          const cardPictureUrl =
            slot.highlightedCardCollectionCard?.anyCard.pictureUrl;
          if (!cardPictureUrl) {
            return null;
          }

          return (
            <AnimatingSlot
              key={slot.slug}
              cardPictureUrl={cardPictureUrl}
              shouldAnimate={
                collectionAnimatingCardIndex !== undefined &&
                slot.highlightedCardCollectionCard?.anyCard.slug ===
                  currentAnimatingCardSlug
              }
              onAnimationEnd={onCardAnimationEnd}
            />
          );
        }
        if (isFilled) {
          return (
            <CardImg
              key={slot.slug}
              src={slot.highlightedCardCollectionCard?.anyCard.pictureUrl}
              width={160}
            />
          );
        }
        return <PlaceholderSlot key={slot.slug} slot={slot} />;
      })}
    </Grid>
  );
};

CollectionAnimationClub.fragments = {
  cardCollection: gql`
    fragment CollectionAnimationClub_cardCollection on CardCollection {
      slug
      userCardCollection(forUserSlug: $user) {
        id
        slotsCount
        slots {
          slug
          highlightedCardCollectionCard {
            id
            anyCard {
              slug
              pictureUrl
            }
          }
          cardCollectionCardsCount
          ...PlaceholderSlot_userCardCollectionSlot
        }
      }
      ...TeamSlot_cardCollection
    }
    ${TeamSlot.fragments.cardCollection}
    ${PlaceholderSlot.fragments.userCardCollectionSlot}
  ` as TypedDocumentNode<CollectionAnimationClub_cardCollection>,
  anyCardInterface: gql`
    fragment CollectionAnimationClub_anyCardInterface on AnyCardInterface {
      slug
      anyTeam {
        slug
      }
      ...TeamSlot_anyCardInterface
    }
    ${TeamSlot.fragments.anyCardInterface}
  ` as TypedDocumentNode<CollectionAnimationClub_anyCardInterface>,
};
